import { redactSensitiveUrl } from '@commander/utils';
import type { Breadcrumb, Event } from '@sentry/react';

const BREADCRUMB_URL_KEYS = ['from', 'to', 'url'] as const;

/**
 * Redacts sensitive query values (see `redactSensitiveUrl`) from the page URLs Sentry
 * attaches to an event: `request.url` and its Referer header, and stack frame filenames
 * (an inline-script error is filed under the page URL).
 */
export function redactEventUrls<T extends Event>(event: T): T {
  const request = event.request;
  if (request?.url) request.url = redactSensitiveUrl(request.url);
  const referer = request?.headers?.Referer;
  if (request?.headers && referer) request.headers.Referer = redactSensitiveUrl(referer);
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      if (frame.filename) frame.filename = redactSensitiveUrl(frame.filename);
    }
  }
  return event;
}

/** Navigation breadcrumbs carry `from`/`to` page URLs; fetch and xhr ones carry `url`. */
export function redactBreadcrumbUrls(breadcrumb: Breadcrumb): Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = data[key];
    if (typeof value === 'string') data[key] = redactSensitiveUrl(value);
  }
  return breadcrumb;
}
